import { useSelector } from 'react-redux';
import { styled } from '@mui/material/styles';
import { Typography } from '@components/Typography';
import TableContainer from '@mui/material/TableContainer';
import ActionIconButton from '@components/ActionIconButton';
import EditIcon from '@mui/icons-material/EditRounded';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Table, getPaginationLabels } from '@components/Table';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/DeleteRounded';
import { useTranslation } from '@hooks';
import { FORM_TYPE } from '@features/ReplyTemplateManageModal';
import { formatEntities } from '@helpers/formatEntitiesToString';

const CroppedText = styled(Typography)({
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: 400,
    '@media (max-width: 1024px)': {
        maxWidth: 200,
    },
});

const getTableHeader = t => {
    return [
        {
            key: 0,
            width: '15%',
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.templateName')}
                </Typography>
            ),
        },
        {
            key: 1,
            width: '50%',
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.message', { count: 1 })}
                </Typography>
            ),
        },
        {
            key: 2,
            width: '25%',
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('ReviewsReplyTemplates.linkedAutoreplies')}
                </Typography>
            ),
        },
        {
            key: 3,
            width: '10%',
            component: <Typography align="left" variant="subtitle2"></Typography>,
        },
    ];
};

const getTableBody = (data, onClickAction, isUser, t) => {
    return data.map(template => [
        {
            component: (
                <Typography variant="subtitle2" align="left">
                    {template.name}
                </Typography>
            ),
        },
        {
            component: (
                <CroppedText variant="body2" align="left">
                    {template.text}
                </CroppedText>
            ),
        },
        {
            component: (
                <Typography style={{ width: 220 }} variant="body2" align="left">
                    {formatEntities(template.rules, { labelKey: 'name' })}
                </Typography>
            ),
        },
        ...(!isUser
            ? [
                  {
                      component: (
                          <Typography align="right" style={{ display: 'flex' }}>
                              <ActionIconButton
                                  title={t('common.edit')}
                                  onClick={() => onClickAction(template, FORM_TYPE.EDIT)}
                              >
                                  <EditIcon viewBox="0 0 22 22" />
                              </ActionIconButton>
                              <ActionIconButton
                                  title={t('common.duplicate')}
                                  sx={{ width: '36px' }}
                                  onClick={() => onClickAction(template, FORM_TYPE.EDIT_COPY)}
                              >
                                  <ContentCopyIcon sx={{ fontSize: 18 }} />
                              </ActionIconButton>
                              <ActionIconButton
                                  title={t('common.delete')}
                                  onClick={() => onClickAction(template, FORM_TYPE.DELETE)}
                              >
                                  <DeleteIcon sx={{ path: { fill: 'rgba(0,0,0,.6)' } }} />
                              </ActionIconButton>
                          </Typography>
                      ),
                  },
              ]
            : [
                  {
                      component: (
                          <Typography align="right">
                              <ActionIconButton
                                  title={t('common.view')}
                                  onClick={() => onClickAction(template, FORM_TYPE.VIEW)}
                              >
                                  <VisibilityIcon viewBox="0 0 22 22" />
                              </ActionIconButton>
                          </Typography>
                      ),
                  },
              ]),
    ]);
};

const ReplyTemplateTable = ({
    setModal,
    setSelectedTemplate,
    setPage,
    page,
    setRowsPerPage,
    rowsPerPage,
    setDeleteModalOpen,
    isUser,
}) => {
    const { t } = useTranslation();
    const { data, loading, totalCount, pages } = useSelector(state => state.replyTemplates);

    const onClickAction = (template, type) => {
        setSelectedTemplate({
            ...template,
            ...(type === FORM_TYPE.EDIT_COPY && { name: `${template.name} (${t('common.copy')})` }),
        });
        if (type === FORM_TYPE.DELETE) {
            setDeleteModalOpen(true);
            return;
        }
        setModal({ open: true, type });
    };

    const onPageChange = (_, newPage) => setPage(newPage);

    const onRowsPerPageChange = event => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    return (
        <TableContainer>
            <Table
                withHover
                columnNames={getTableHeader(t)}
                data={data}
                rows={getTableBody(data, onClickAction, isUser, t)}
                onRowClick={template => onClickAction(template, isUser ? FORM_TYPE.VIEW : FORM_TYPE.EDIT)}
                isDataLoading={loading}
                withPagination
                paginationProps={{
                    colSpan: 6,
                    count: totalCount,
                    rowsPerPage,
                    page,
                    onPageChange,
                    onRowsPerPageChange,
                    ...getPaginationLabels(t),
                    pages,
                }}
            />
        </TableContainer>
    );
};

export default ReplyTemplateTable;
